import React from 'react';
import { motion } from 'motion/react';
import { Calendar, Clock, ExternalLink, Mail, Sparkles } from 'lucide-react';
import { SEOHead } from '../Components/common/SEOHead';
import './Blog.css';

interface BlogPost {
  title: string;
  content: string[];
  links: string[];
  date: string;
  excerpt?: string;
  category?: string;
  readTime?: string;
}

const blogData: BlogPost[] = [
  {
    title: "Even With Setbacks, We Keep Shipping",
    excerpt: "Because great Power BI visuals don't happen by accident. How we overcome platform restrictions and continue delivering quality.",
    content: [
      "Because great Power BI visuals don't happen by accident.",
      "Over the past two months, we've faced platform-level restrictions from Microsoft that directly affected us as a publisher of custom visuals.",
      "These limitations slowed us down - but didn't stop us.",
      "We've been working closely with Microsoft to lift these restrictions, so that our users can continue receiving updates, improvements, and entirely new visuals - without compromise.",
      "<strong>Our mission remains the same:</strong>",
      "<ul><li>Deliver high-quality, well-supported visuals</li><li>Respond fast to feedback</li><li>Build tools that make reporting easier, not harder</li></ul>",
      "Right now, we're developing two new visuals - and listening carefully to your feedback on [link=Your Timeline Slicer]. Every comment helps us improve 💛",
      "📬<strong>Some users reported our email replies landing in spam.</strong><br/>Please know: <strong>we respond to every question</strong> - even if it doesn't always reach your inbox.",
      "We're here for the long haul. And we're not done yet.",
      "<em>Best regards, The Based On Team</em>"
    ],
    links: ["https://appsource.microsoft.com/en-us/product/power-bi-visuals/based-on.based_on_your_timeline"],
    date: "2025-05-08",
    category: "Company Updates",
    readTime: "5 min read"
  },
  {
    title: "Get a Free Month for Your Feedback!",
    excerpt: "We're looking for Power BI users to test our Timeline Slicer and share their honest feedback with exclusive benefits.",
    content: [
      "We're looking for Power BI users to test our Timeline Slicer and share their honest feedback!",
      "You will get <strong>one month free access</strong> to the visual and a <strong>20% discount</strong> on future months for the existing plan. In return, we ask you to try it in your dashboards, leave a review on AppSource, and share any issues or improvement ideas.",
      "If you are interested, [link=Contact Us] for more information.",
      "[link=Try it now on AppSource]",
      "We appreciate your support and look forward to your feedback.",
      "<em>Signing in to the private plan is available until May 31st, 2025.</em>"
    ],
    links: ["mailto:support@based-on.net", "https://appsource.microsoft.com/en-us/product/power-bi-visuals/based-on.based_on_your_timeline"],
    date: "2025-02-22",
    category: "Announcements",
    readTime: "3 min read"
  },
  {
    title: "Reimagining Power BI Visuals: Why We Built Our Own",
    excerpt: "Learn about our journey from struggling with existing custom visuals to creating our own Timeline Slicer with up to 40% space savings.",
    content: [
      "We've been working with Power BI for a long time and often struggled with custom visuals - either they had a confusing design, unexpected bugs, or were just too complex for what we needed. At the same time, the built-in visuals weren't always enough. Every time we looked for a better solution, we either found partial fixes or overly complicated tools.",
      "So, we decided to create our own.",
      "Our first visual is the <strong>Timeline Slicer</strong> - a simple, lightweight alternative to Microsoft's solution. We've completely reworked it to optimize performance and improve the user experience while keeping all the essential functionality. We also built it with the future in mind, making it easier to expand and improve over time.",
      "For example, in our initial release, we introduced a feature that <strong>reduces the space required by the visual by up to 40%</strong>, without sacrificing any functionality.",
      "This is just the beginning, and we're excited to continue making Power BI easier and more enjoyable to use!",
      "We'd love to hear your thoughts - what do you think about custom visuals in Power BI? What features would you like to see? Let's discuss it on [link=LinkedIn] or [link=Contact Us] directly!"
    ],
    links: [
      "https://www.linkedin.com/in/iuliia-strunnikova/",
      "mailto:support@based-on.net"
    ],
    date: "2025-02-10",
    category: "Product Insights",
    readTime: "6 min read"
  }
];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const renderPostContent = (post: BlogPost) => {
  let linkIndex = 0;

  return post.content.map((content, paragraphIndex) => {
    const replaceLinks = (value: string) => value.replace(/\[link=([^\]]+)\]/g, (_match, text) => {
      const href = post.links[linkIndex] ?? '';
      linkIndex++;
      const isExternal = href.startsWith('http');
      return `<a href="${href}" ${isExternal ? 'target="_blank" rel="noopener noreferrer"' : ''} class="blog-inline-link">${text}</a>`;
    });

    if (content.includes('<') && content.includes('>')) {
      return (
        <div
          key={paragraphIndex}
          className="blog-post-paragraph"
          dangerouslySetInnerHTML={{ __html: replaceLinks(content) }}
        />
      );
    }

    return (
      <p className="blog-post-paragraph" key={paragraphIndex}>
        {content.split(/\[link=([^\]]+)\]/).map((part, index) => {
          if (index % 2 === 1) {
            const href = post.links[linkIndex] ?? '';
            linkIndex++;
            const isExternal = href.startsWith('http');
            return (
              <a
                key={`${part}-${index}`}
                className="blog-inline-link"
                href={href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
              >
                {part}
                {isExternal && <ExternalLink size={13} />}
              </a>
            );
          }
          return part;
        })}
      </p>
    );
  });
};

export function Blog() {
  return (
    <div className="blog-page">
      <SEOHead
        title="Blog - Insights & Updates"
        description="Insights, updates, and stories from our journey building better Power BI visuals and productivity tools. Learn about custom visuals, best practices, and product updates."
        keywords="Power BI blog, Power BI visuals tips, custom visuals development, Power BI best practices, data visualization insights"
        ogType="website"
        canonical="https://based-on.net/blog"
      />

      <section className="blog-hero">
        <div className="blog-dot-grid blog-dot-grid-left" aria-hidden="true" />
        <div className="blog-dot-grid blog-dot-grid-right" aria-hidden="true" />
        <div className="blog-wave" aria-hidden="true" />

        <motion.div
          className="blog-hero-content"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <div className="blog-pill">
            <Sparkles size={15} />
            <span>Insights &amp; Updates</span>
          </div>
          <h1>Our Blog</h1>
          <div className="blog-title-mark" />
          <p>
            Insights, updates, and stories from our journey building better Power BI visuals and productivity tools.
          </p>
        </motion.div>
      </section>

      <section className="blog-main">
        <div className="blog-list">
          {blogData.map((post, index) => (
            <motion.article
              key={post.date}
              className="blog-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: index * 0.08, ease: 'easeOut' }}
              viewport={{ once: true }}
            >
              <div className="blog-meta">
                <span className="blog-category">{post.category}</span>
                <span>
                  <Calendar size={16} />
                  {formatDate(post.date)}
                </span>
                <span>
                  <Clock size={16} />
                  {post.readTime}
                </span>
              </div>

              <h2>{post.title}</h2>
              <div className="blog-content">{renderPostContent(post)}</div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="blog-contact">
        <motion.div
          className="blog-contact-card"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <div className="blog-contact-icon">
            <Mail size={32} />
          </div>
          <h2>Get in Touch</h2>
          <p>
            Have questions about our Power BI visuals or want to share feedback?
            We'd love to hear from you.
          </p>
          <a className="blog-contact-button" href="mailto:support@based-on.net">
            <Mail size={18} />
            <span>Contact Us</span>
          </a>
        </motion.div>
      </section>
    </div>
  );
}
